"use client";
import { useState, useRef } from "react";
import { useRouter } from "next/navigation";
import { createAbility, updateAbility } from "@/lib/api/abilities";
import { ApiError } from "@/lib/api/client";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useFocusOnceSettled } from "@/hooks/useFocusOnceSettled";

const ICONS = [
  "🪐",
  "💼",
  "📞",
  "🏠",
  "🔍",
  "📊",
  "💡",
  "⚙️",
  "🪶",
  "⚖️",
  "💰",
  "📣",
];

/** A slug from a name: lowercase letters, digits and hyphens, at most 64 characters (the server's rule). */
export function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 64)
    .replace(/-+$/, "");
}

/** What a refused create means, in plain words, by the server's structured `error` code. */
const CREATE_REFUSALS: Record<string, string> = {
  slug_invalid: "Use only lowercase letters, digits and hyphens in the slug.",
  slug_reserved: "That slug belongs to a built-in or shared ability. Pick another.",
  "duplicate slug": "You already have an ability with that slug. Pick another.",
};

export function createErrorMessage(err: unknown): string {
  if (err instanceof ApiError && err.code !== null && CREATE_REFUSALS[err.code]) return CREATE_REFUSALS[err.code]!;
  return "The ability could not be created. Try again.";
}

export function CreateAbilityModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated?: (slug: string) => void;
}) {
  const router = useRouter();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [description, setDescription] = useState("");
  const [icon, setIcon] = useState(ICONS[0]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const dialogContentRef = useRef<HTMLDivElement>(null);

  useFocusOnceSettled(open, dialogContentRef, () => {
    nameInputRef.current?.focus();
  });

  const effectiveSlug = slugTouched ? slug : slugify(name);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const ability = await createAbility({
        name: name.trim(),
        slug: effectiveSlug,
        description: description.trim(),
      });
      // Server doesn't accept icon on create — patch after if non-default
      if (icon && icon !== ICONS[0]) {
        try {
          await updateAbility(ability.id, { icon });
        } catch {
          // Non-fatal: ability is created, icon update can be retried in detail page
        }
      }
      onCreated?.(ability.slug);
      router.push(`/abilities/${ability.slug}`);
      onClose();
    } catch (err: unknown) {
      setError(createErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(val) => !val && onClose()}>
      <DialogContent
        ref={dialogContentRef}
        className="z-[200] sm:max-w-[460px]"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
        }}
      >
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Create a new Ability</DialogTitle>
            <DialogDescription>
              An Ability bundles your skills, agents, MCPs and hooks. Start
              empty and add things as you go.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-y-4">
            <Field label="Name">
              <input
                ref={nameInputRef}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Real Estate"
                className="w-full h-9 px-3 bg-bg-subtle border border-border-subtle rounded-12 text-3.5 text-fg-default placeholder:text-fg-faint focus:outline-none focus:border-border-strong transition-colors"
                required
              />
            </Field>

            <Field label="Slug">
              <input
                type="text"
                value={effectiveSlug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setSlug(e.target.value);
                }}
                placeholder="real-estate"
                className="w-full h-9 px-3 bg-bg-subtle border border-border-subtle rounded-12 text-3.5 text-fg-default placeholder:text-fg-faint focus:outline-none focus:border-border-strong transition-colors"
                required
              />
              <p className="min-h-8 text-3 text-fg-muted mt-1.5">
                Skills inside this ability will be addressable as{" "}
                <span className="font-medium">
                  /{effectiveSlug || "slug"}:skill-name
                </span>
              </p>
            </Field>

            <Field label="Description">
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="Enter Description"
                className="w-full px-3 py-2 bg-bg-subtle border border-border-subtle rounded-12 text-3.5 text-fg-default placeholder:text-fg-faint focus:outline-none focus:border-border-strong transition-colors resize-none"
                required
              />
            </Field>

            <Field label="Icon">
              <div className="flex flex-wrap gap-1.5">
                {ICONS.map((i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setIcon(i)}
                    className={cn(
                      "w-9 h-9 rounded-12 grid place-items-center text-4.5 transition-colors ring-2 ring-offset-2",
                      icon === i
                        ? "bg-fg-default text-bg-base ring-fg-default ring-offset-bg-base"
                        : "bg-bg-subtle hover:bg-bg-muted ring-transparent ring-offset-transparent"
                    )}
                  >
                    {i}
                  </button>
                ))}
              </div>
            </Field>

            {error && <div className="text-3.5 text-status-error">{error}</div>}
          </div>

          <DialogFooter>
            <Button variant="secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || !name.trim() || !description.trim()}
            >
              {submitting ? "Creating…" : "Create Ability"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="">
      <label className="block text-3 uppercase tracking-wider font-semibold text-fg-subtle mb-1.5">
        {label}
      </label>
      {children}
    </div>
  );
}
